import React, {useContext, useEffect, useMemo, useState} from 'react';
import {Outlet, useLocation, useNavigate, useParams} from 'react-router-dom';
import {Alert, Button, ButtonGroup, IconButton} from '@mui/material';
import {Cancel, ChevronRight, Delete} from '@mui/icons-material';
import {
    GetContractTemplatesQueryQuery,
    useDeleteContractTemplateMutation,
    useGetContractTemplatesQueryQuery
} from '../../generated/graphql';
import {ToolbarContext, ToolbarEntry} from '../../contexts/toolbar-context';
import {useUserContext} from '../../contexts/user-context';
import { useToast } from "../../contexts/toast";
import FieldTable from '../../components/field-table';
import {FieldTableColumnProps} from '../../components/FieldTable/FieldTableTypes';
import {ModalWindow} from '../../components/ModalWindow';

type ContractTemplateRow = NonNullable<GetContractTemplatesQueryQuery['contractTemplates'][number]>;
type ContractTemplateListRow = ContractTemplateRow & {block_count: number};

const ContractTemplates = () => {
    const user = useUserContext();
    const navigate = useNavigate();
    const params = useParams();
    const location = useLocation();
    const toolbarContext = useContext(ToolbarContext);
    const {toast, dismiss} = useToast();
    const {data, loading, refetch} = useGetContractTemplatesQueryQuery({fetchPolicy: 'network-only'});
    const [deleteTemplate, {loading: deleting}] = useDeleteContractTemplateMutation();
    const [deleteId, setDeleteId] = useState<string | null>(null);

    const canEdit = user.hasPermission('contracts.edit');
    const search = location.search || '';

    const templates: ContractTemplateListRow[] = useMemo(
        () => (data?.contractTemplates || [])
            .filter((t): t is ContractTemplateRow => !!t)
            .map((t) => ({
                ...t,
                block_count: (t.sections || []).reduce((n, s) => n + ((s?.blocks || []).length), 0)
            })),
        [data]);

    useEffect(() => {
        const primary: ToolbarEntry[] = [];
        if (canEdit) {
            primary.push({
                type: 'button',
                label: 'New',
                tooltip: 'New template',
                icon: 'file-add',
                callback: () => navigate('/database/contracts/new' + search)
            });
        }
        toolbarContext.setPrimary({
            primary,
            title: 'Contract Templates',
            primaryUrl: '/database/contracts',
            secondaryUrl: params.id
        });
    }, [canEdit, params.id, search]);

    const doDelete = async () => {
        if (!deleteId) return;
        toast('Deleting template', {id: 'DEL_TEMPLATE'});
        try {
            await deleteTemplate({variables: {id: deleteId}});
            toast('Template deleted', {id: 'DEL_TEMPLATE', variant: 'success'});
            await refetch();
        } catch (e) {
            toast('Template deletion failed', {id: 'DEL_TEMPLATE', variant: 'error'});
        } finally {
            setDeleteId(null);
        }
    };

    const columns: FieldTableColumnProps<ContractTemplateListRow>[] = [
        {
            accessor: 'name',
            header: 'Name',
            dataType: 'link',
            options: {
                getLink: ({row}) => ({
                    pathname: '/database/contracts/' + row?.id,
                    search
                })
            }
        },
        {accessor: 'description', header: 'Description', dataType: 'default', className: 'column-width-wide-plus'},
        {accessor: 'block_count', header: 'Blocks', dataType: 'default'}
    ];

    const isEditing = !!params.id;
    return (
        <div className={'side-editor' + (isEditing ? ' is-editing' : '')}>
            <div className="side-editor__list is-scrollable">
                <FieldTable className="contract-templates-list" stickyFooter={true}
                            hasDelete={canEdit} loading={loading} name="contractTemplates"
                            doDelete={setDeleteId}
                            values={templates} columns={columns}
                            getRowClass={(row) => row?.id === params.id ? 'row-selected' : ''}/>
            </div>
            <div className="side-editor__separator" style={{display: isEditing ? 'block' : 'none'}}>
                <IconButton size="small" onClick={() => navigate('/database/contracts' + search)}>
                    <ChevronRight/>
                </IconButton>
            </div>
            <div className="side-editor__editor is-scrollable">
                <Outlet/>
            </div>
            <ModalWindow isOpen={!!deleteId} onClose={() => setDeleteId(null)} header="Delete template"
                         actions={<ButtonGroup>
                             <Button key="d1" color="error" onClick={doDelete} disabled={deleting} startIcon={<Delete/>}>Delete</Button>
                             <Button key="d2" onClick={() => setDeleteId(null)} startIcon={<Cancel/>}>Cancel</Button>
                         </ButtonGroup>}>
                <Alert severity="warning">Delete this template? Campaigns that already applied it keep their blocks.</Alert>
            </ModalWindow>
        </div>
    );
};

export default ContractTemplates;
